/*!
 * Shop the Look — encapsulated video carousel
 * -----------------------------------------------------------------------
 * Five custom elements, each with its own Shadow DOM (so styles/behaviour
 * never leak into the rest of the theme, and vice versa):
 *
 *   moast-video     lazy <video> — real src is only attached while
 *                   preload="auto"; goes back to preload="none" (no
 *                   network request) the moment the slide leaves view.
 *   moast-mute      mute/unmute toggle, fires a composed "moast:mutechange"
 *   moast-media     one card: lays out video + mute + product tag + a
 *                   center "open in player" affordance.
 *   moast-products  bottom-left caption tag (name + price), links out.
 *   moast-carousel  scroll-snap track, arrow nav, IntersectionObserver
 *                   that flips preload on/off per slide.
 *   moast-player    the dedicated full player opened on card click.
 * -----------------------------------------------------------------------
 */
(() => {
  'use strict';

  /* ------------------------------------------------------------------ *
   * <moast-video>
   * ------------------------------------------------------------------ */
  class MoastVideo extends HTMLElement {
    static get observedAttributes() {
      return ['preload', 'poster'];
    }

    constructor() {
      super();
      const root = this.attachShadow({ mode: 'open' });
      root.innerHTML = `
        <style>
          :host { display:block; width:100%; height:100%; }
          video {
            width:100%; height:100%;
            object-fit:cover;
            display:block;
            background:#eee;
          }
        </style>
        <video muted playsinline preload="none"></video>
      `;
      this._video = root.querySelector('video');
    }

    connectedCallback() {
      this._video.setAttribute('poster', this.getAttribute('poster') || '');
      this._applyPreload(this.getAttribute('preload') || 'none');
    }

    attributeChangedCallback(name, oldVal, newVal) {
      if (oldVal === newVal) return;
      if (name === 'preload') this._applyPreload(newVal);
      if (name === 'poster') this._video.setAttribute('poster', newVal || '');
    }

    /** The only place the real network request is decided. */
    _applyPreload(mode) {
      const realSrc = this.getAttribute('data-src');
      const realType = this.getAttribute('data-type') || 'video/mp4';
      if (!realSrc) return;

      if (mode === 'auto') {
        // Slide is active/near-active: attach the real source so the
        // browser can start fetching it.
        if (this._video.dataset.attached !== 'true') {
          this._video.innerHTML = `<source src="${realSrc}" type="${realType}">`;
          this._video.setAttribute('preload', 'auto');
          this._video.load();
          this._video.dataset.attached = 'true';
        }
      } else {
        // Slide scrolled out of view: detach the source so nothing
        // downloads in the background. Poster image keeps showing.
        if (this._video.dataset.attached === 'true') {
          this._video.pause();
          this._video.removeAttribute('src');
          this._video.innerHTML = '';
          this._video.setAttribute('preload', 'none');
          this._video.load();
          this._video.dataset.attached = 'false';
        }
      }
    }

    get videoEl() {
      return this._video;
    }

    set muted(val) {
      this._video.muted = !!val;
    }

    get muted() {
      return this._video.muted;
    }
  }

  /* ------------------------------------------------------------------ *
   * <moast-mute>
   * ------------------------------------------------------------------ */
  class MoastMute extends HTMLElement {
    static get observedAttributes() {
      return ['muted'];
    }

    constructor() {
      super();
      const root = this.attachShadow({ mode: 'open' });
      root.innerHTML = `
        <style>
          :host { display:block; }
          button {
            width:28px; height:28px;
            border-radius:999px;
            border:none;
            background:rgba(0,0,0,0.55);
            color:#fff;
            display:flex;
            align-items:center;
            justify-content:center;
            cursor:pointer;
            padding:0;
          }
          button:focus-visible { outline:2px solid #fff; outline-offset:2px; }
          svg { width:14px; height:14px; }
        </style>
        <button type="button" aria-label="Toggle sound" aria-pressed="false">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M11 5 6 9H2v6h4l5 4V5Z"></path>
            <line class="mute-x" x1="23" y1="9" x2="17" y2="15"></line>
            <line class="mute-x" x1="17" y1="9" x2="23" y2="15"></line>
            <path class="mute-waves" d="M15.5 8.5a5 5 0 0 1 0 7" style="display:none"></path>
          </svg>
        </button>
      `;
      this._btn = root.querySelector('button');
      this._crossA = root.querySelector('.mute-x:nth-of-type(1)');
      this._crossB = root.querySelectorAll('.mute-x')[1];
      this._waves = root.querySelector('.mute-waves');

      this._btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const isMuted = this.getAttribute('muted') !== 'false';
        this.setAttribute('muted', String(!isMuted));
        this.dispatchEvent(
          new CustomEvent('moast:mutechange', {
            bubbles: true,
            composed: true,
            detail: { muted: !isMuted },
          })
        );
      });
    }

    connectedCallback() {
      this._render();
    }

    attributeChangedCallback() {
      this._render();
    }

    _render() {
      const isMuted = this.getAttribute('muted') !== 'false';
      this._btn.setAttribute('aria-pressed', String(isMuted));
      this._btn.setAttribute('aria-label', isMuted ? 'Unmute video' : 'Mute video');
      const svgLines = this.shadowRoot.querySelectorAll('.mute-x');
      const waves = this.shadowRoot.querySelector('.mute-waves');
      svgLines.forEach((l) => (l.style.display = isMuted ? 'block' : 'none'));
      if (waves) waves.style.display = isMuted ? 'none' : 'block';
    }
  }

  /* ------------------------------------------------------------------ *
   * <moast-products>
   * ------------------------------------------------------------------ */
  class MoastProducts extends HTMLElement {
    constructor() {
      super();
      const root = this.attachShadow({ mode: 'open' });
      const name = this.getAttribute('name') || '';
      const price = this.getAttribute('price') || '';
      const compareAt = this.getAttribute('compare-at-price') || '';
      const href = this.getAttribute('href') || '#';
      const thumb = this.getAttribute('thumb') || '';

      root.innerHTML = `
        <style>
          :host { display:block; }
          a {
            display:flex;
            align-items:center;
            gap:8px;
            background:#fff;
            border-radius:8px;
            padding:6px 10px 6px 6px;
            text-decoration:none;
            color:#1a1a1a;
            box-shadow:0 2px 8px rgba(0,0,0,0.15);
            max-width:100%;
          }
          img {
            width:28px; height:28px;
            border-radius:6px;
            object-fit:cover;
            flex:none;
            background:#f2f2f2;
          }
          .meta {
            min-width:0;
            display:flex;
            flex-direction:column;
            line-height:1.2;
          }
          .name {
            font-size:12px;
            font-weight:600;
            white-space:nowrap;
            overflow:hidden;
            text-overflow:ellipsis;
          }
          .price { font-size:11px; display:flex; gap:5px; align-items:baseline; }
          .price .compare { text-decoration:line-through; color:#9a9a9a; }
          .price .sale { color:#1a1a1a; font-weight:600; }
        </style>
        <a href="${href}" aria-label="Shop ${name}">
          ${thumb ? `<img src="${thumb}" alt="" loading="lazy">` : ''}
          <span class="meta">
            <span class="name">${name}</span>
            <span class="price">
              ${compareAt ? `<span class="compare">${compareAt}</span>` : ''}
              <span class="sale">${price}</span>
            </span>
          </span>
        </a>
      `;

      root.querySelector('a').addEventListener('click', (e) => {
        // Let the navigation happen, just don't let it also open the player.
        e.stopPropagation();
      });
    }
  }

  /* ------------------------------------------------------------------ *
   * <moast-media>  — one card
   * ------------------------------------------------------------------ */
  class MoastMedia extends HTMLElement {
    constructor() {
      super();
      const root = this.attachShadow({ mode: 'open' });
      root.innerHTML = `
        <style>
          :host {
            display:block;
            position:relative;
            aspect-ratio: 9 / 16;
            border-radius:14px;
            overflow:hidden;
            background:#eee;
            cursor:pointer;
          }
          ::slotted(moast-video) {
            position:absolute; inset:0;
          }
          .mute-slot { position:absolute; top:8px; right:8px; z-index:2; }
          .products-slot { position:absolute; left:8px; bottom:8px; right:8px; z-index:2; }
          .play {
            position:absolute; inset:0;
            display:flex; align-items:center; justify-content:center;
            z-index:1;
            pointer-events:none;
          }
          .play__circle {
            width:44px; height:44px;
            border-radius:999px;
            background:rgba(0,0,0,0.45);
            display:flex; align-items:center; justify-content:center;
          }
          .play svg { width:18px; height:18px; fill:#fff; margin-left:2px; }
        </style>
        <slot name="video"></slot>
        <div class="play" aria-hidden="true">
          <span class="play__circle">
            <svg viewBox="0 0 24 24"><path d="M8 5v14l11-7z"></path></svg>
          </span>
        </div>
        <div class="mute-slot"><slot name="mute"></slot></div>
        <div class="products-slot"><slot name="products"></slot></div>
      `;

      this.addEventListener('click', (e) => this._handleClick(e));
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this._openPlayer();
        }
      });

      this.tabIndex = 0;
      this.setAttribute('role', 'button');
      this.setAttribute('aria-label', 'Click to open in player');
    }

    _handleClick(e) {
      const path = e.composedPath();
      const hitsInteractive = path.some(
        (el) => el.tagName === 'MOAST-MUTE' || el.tagName === 'MOAST-PRODUCTS'
      );
      if (hitsInteractive) return;
      this._openPlayer();
    }

    _openPlayer() {
      const videoEl = this.querySelector('moast-video');
      const productsEl = this.querySelector('moast-products');
      if (!videoEl) return;

      this.dispatchEvent(
        new CustomEvent('moast:open', {
          bubbles: true,
          composed: true,
          detail: {
            src: videoEl.getAttribute('data-src'),
            type: videoEl.getAttribute('data-type') || 'video/mp4',
            poster: videoEl.getAttribute('poster') || '',
            lookName: this.getAttribute('look-name') || '',
            product: productsEl
              ? {
                  href: productsEl.getAttribute('href'),
                  name: productsEl.getAttribute('name'),
                  price: productsEl.getAttribute('price'),
                  compareAt: productsEl.getAttribute('compare-at-price'),
                  thumb: productsEl.getAttribute('thumb'),
                }
              : null,
          },
        })
      );
    }
  }

  /* ------------------------------------------------------------------ *
   * <moast-player>  — dedicated player (modal)
   * ------------------------------------------------------------------ */
  class MoastPlayer extends HTMLElement {
    constructor() {
      super();
      const root = this.attachShadow({ mode: 'open' });
      root.innerHTML = `
        <style>
          :host { display:block; }
          .backdrop {
            position:fixed; inset:0;
            background:rgba(0,0,0,0.85);
            display:flex; align-items:center; justify-content:center;
            padding:24px;
          }
          .panel {
            position:relative;
            width:min(92vw, 420px);
            aspect-ratio:9/16;
            border-radius:16px;
            overflow:hidden;
            background:#000;
          }
          video { width:100%; height:100%; object-fit:contain; background:#000; }
          .close {
            position:absolute; top:10px; right:10px;
            width:32px; height:32px;
            border-radius:999px;
            border:none;
            background:rgba(255,255,255,0.15);
            color:#fff;
            display:flex; align-items:center; justify-content:center;
            cursor:pointer;
            z-index:3;
          }
          .tag {
            position:absolute; left:10px; bottom:10px; right:10px; z-index:3;
          }
          .tag a {
            display:flex; align-items:center; gap:8px;
            background:#fff; border-radius:8px; padding:8px 12px 8px 8px;
            text-decoration:none; color:#1a1a1a;
          }
          .tag img { width:32px; height:32px; border-radius:6px; object-fit:cover; }
          .tag .name { font-size:13px; font-weight:600; }
          .tag .price { font-size:12px; display:flex; gap:6px; }
          .tag .compare { text-decoration:line-through; color:#9a9a9a; }
        </style>
        <div class="backdrop">
          <div class="panel">
            <button class="close" type="button" aria-label="Close player">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 6 6 18M6 6l12 12"/></svg>
            </button>
            <video playsinline controls></video>
            <div class="tag"></div>
          </div>
        </div>
      `;
      this._backdrop = root.querySelector('.backdrop');
      this._video = root.querySelector('video');
      this._tag = root.querySelector('.tag');

      root.querySelector('.close').addEventListener('click', () => this.close());
      this._backdrop.addEventListener('click', (e) => {
        if (e.target === this._backdrop) this.close();
      });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !this.hidden) this.close();
      });
    }

    open(detail) {
      if (!detail || !detail.src) return;
      this._video.innerHTML = `<source src="${detail.src}" type="${detail.type || 'video/mp4'}">`;
      this._video.setAttribute('poster', detail.poster || '');
      this._video.muted = false;
      this._video.load();

      if (detail.product && detail.product.href) {
        const p = detail.product;
        this._tag.innerHTML = `
          <a href="${p.href}" aria-label="Shop ${p.name || ''}">
            ${p.thumb ? `<img src="${p.thumb}" alt="">` : ''}
            <span>
              <div class="name">${p.name || ''}</div>
              <div class="price">
                ${p.compareAt ? `<span class="compare">${p.compareAt}</span>` : ''}
                <span class="sale">${p.price || ''}</span>
              </div>
            </span>
          </a>`;
      } else {
        this._tag.innerHTML = '';
      }

      this.hidden = false;
      document.body.style.overflow = 'hidden';
      this._video.play().catch(() => {
        /* autoplay-with-sound can be blocked; user can hit play manually */
      });
    }

    close() {
      this._video.pause();
      this._video.removeAttribute('src');
      this._video.innerHTML = '';
      this._video.load();
      this.hidden = true;
      document.body.style.overflow = '';
    }
  }

  /* ------------------------------------------------------------------ *
   * <moast-carousel>
   * ------------------------------------------------------------------ */
  class MoastCarousel extends HTMLElement {
    connectedCallback() {
      this._track = this.querySelector('.moast-track');
      this._prevBtn = this.querySelector('.moast-nav__btn--prev');
      this._nextBtn = this.querySelector('.moast-nav__btn--next');
      this._player = this.parentElement.querySelector('moast-player');
      this._activeVideo = null;

      if (this._prevBtn) this._prevBtn.addEventListener('click', () => this._scrollBy(-1));
      if (this._nextBtn) this._nextBtn.addEventListener('click', () => this._scrollBy(1));

      this.addEventListener('moast:open', (e) => {
        if (this._player) this._player.open(e.detail);
      });

      this._setupObserver();
      this._updateNavState();
      this._track.addEventListener('scroll', () => this._updateNavState(), { passive: true });
      window.addEventListener('resize', () => this._updateNavState());
    }

    _setupObserver() {
      const slides = Array.from(this.querySelectorAll('.moast-slide'));
      if (!slides.length || !('IntersectionObserver' in window)) {
        // Fallback: just activate the first slide's video.
        const first = slides[0] && slides[0].querySelector('moast-video');
        if (first) first.setAttribute('preload', 'auto');
        return;
      }

      const ratios = new Map();

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            ratios.set(entry.target, entry.intersectionRatio);
          });

          let bestSlide = null;
          let bestRatio = 0;
          ratios.forEach((ratio, slide) => {
            if (ratio > bestRatio) {
              bestRatio = ratio;
              bestSlide = slide;
            }
          });

          if (bestSlide && bestRatio > 0.5) {
            const nextVideo = bestSlide.querySelector('moast-video');
            if (nextVideo && nextVideo !== this._activeVideo) {
              if (this._activeVideo) this._activeVideo.setAttribute('preload', 'none');
              nextVideo.setAttribute('preload', 'auto');
              this._activeVideo = nextVideo;
            }
          }
        },
        { root: this._track, threshold: [0, 0.25, 0.5, 0.75, 1] }
      );

      slides.forEach((slide) => observer.observe(slide));
    }

    _scrollBy(dir) {
      const slide = this._track.querySelector('.moast-slide');
      const step = slide ? slide.getBoundingClientRect().width + 14 : 240;
      this._track.scrollBy({ left: dir * step, behavior: 'smooth' });
    }

    _updateNavState() {
      if (!this._prevBtn || !this._nextBtn) return;
      const { scrollLeft, scrollWidth, clientWidth } = this._track;
      this._prevBtn.disabled = scrollLeft <= 4;
      this._nextBtn.disabled = scrollLeft + clientWidth >= scrollWidth - 4;
    }
  }

  customElements.define('moast-video', MoastVideo);
  customElements.define('moast-mute', MoastMute);
  customElements.define('moast-products', MoastProducts);
  customElements.define('moast-media', MoastMedia);
  customElements.define('moast-player', MoastPlayer);
  customElements.define('moast-carousel', MoastCarousel);
})();